/**
 * THE PREVIEW OF THE PUBLIC PAGE, DRAWN (design 24, T28; the owner's pick, 2026-10-11).
 *
 * After the product's public author page (`moonx-web-product/author-mr-moon.html`, its "author hero":
 * the name, the photo beside the chips, the specialization line, "Experience & approach", "Bio",
 * "Writes & researches in", the signs), from ./public-view.ts's answer and nothing else - so what
 * is drawn here is only ever what readers would see. What the profile does not hold is left out
 * rather than faked: "Verified", "On MoonX since", languages spoken, a video greeting and the
 * reader's buttons (write, follow, share). The cost the owner accepted: it is a copy of the
 * website's design, so when that page changes, this one follows.
 *
 * THE FIGURES (batch 2, B2-PREVIEW): the product's row - articles, followers - in its place under
 * the specialization line, from the cabinet's own reads (./public-figures.ts), and only once there
 * are some. Its third figure, the reads, is left out: the public page has none to show - the reader
 * API's `PublicAuthor` (magazine-api.yaml) carries `articleCount` and `followerCount` and no views.
 * "articles" is design 23's word (R56) for the product's "articles & guides".
 *
 * The bar on top says what this is and is the way back to the Author profile. "Back to editing" is
 * a real link to `/apply`, followed in place under the router (`followsInApp`, as `InAppAnchor`).
 *
 * Inks, measured on the grounds they sit on in both schemes (WCAG 4.5:1), all pairs the cabinet has
 * measured before: on `surface` `m2` 6.61 light / 6.31 dark and `accent-hover` 6.08 / 14.17
 * (../../features/support/support.module.css); on `surface2` `m2` 6.03 / 6.21 and `accent-hover`
 * 5.54 / 13.93 (../../features/cabinet/components/kit.module.css). Tokens only, composed at use.
 */
import { Anchor, Button } from '@mantine/core';
import { ArrowLeft, ArrowUp, Eye, Moon, Sun, User } from '@moonx/ui';
import type { MouseEvent, ReactNode } from 'react';
import { formatCount } from '../../features/cabinet/format';
import { followsInApp, useNavigateToHref } from '../../features/shell/in-app-navigation';
import { APPLY_ROUTE } from '../apply';
import type { PublicFigures } from './public-figures';
import type { PublicLink, PublicView } from './public-view';
import { i18n, useTranslation } from '../../i18n';
import type { ENGLISH_RESOURCES } from '../../i18n';
import type { TFunction } from 'i18next';
import classes from './profile-preview.module.css';

/** The bar on top: what this page is, and the way back to the Author profile. */
export function PreviewBar(): ReactNode {
  const { t } = useTranslation('profile');
  const navigate = useNavigateToHref();
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!followsInApp(event)) {
      return;
    }
    event.preventDefault();
    navigate(APPLY_ROUTE.path);
  };
  return (
    <div className={classes.bar}>
      <Eye size={16} aria-hidden="true" className={classes.barMark} />
      <p className={classes.barLine}>{t('preview.line')}</p>
      <Button
        component="a"
        href={APPLY_ROUTE.path}
        onClick={follow}
        variant="default"
        radius="xl"
        size="sm"
        leftSection={<ArrowLeft size={14} aria-hidden="true" />}
      >
        {t('preview.back')}
      </Button>
    </div>
  );
}

/** One line of "Details". */
interface DetailRow {
  readonly label: string;
  readonly value: ReactNode;
}

/** `https://mirahalloway.example/` -> `mirahalloway.example`: the address, as a reader reads it. */
function shownAddress(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.host}${path}`;
  } catch {
    return url;
  }
}

function detailRows(view: PublicView, t: TFunction<'profile'>): readonly DetailRow[] {
  const rows: DetailRow[] = [];
  // "Based in", as the product's page says it (design 25: the primary location, in the time zone's
  // place - the zone itself is not drawn any more).
  if (view.primaryLocation !== null) {
    rows.push({ label: t('preview.details.basedIn'), value: view.primaryLocation });
  }
  if (view.pronouns !== null) {
    rows.push({ label: t('preview.details.pronouns'), value: view.pronouns });
  }
  for (const link of view.links) {
    rows.push({
      label: linkKindName(link),
      value: (
        <Anchor href={link.url} target="_blank" rel="noopener noreferrer" size="sm">
          {shownAddress(link.url)}
        </Anchor>
      ),
    });
  }
  return rows;
}

/** "12 years of practice", "1 year of practice" - and 0, which is shown too, as less than a year. */
function yearsWords(years: number, t: TFunction<'profile'>): string {
  if (years === 0) return t('preview.lessThanAYear');
  return t('preview.years', { count: years });
}

type ApplicationTexts = (typeof ENGLISH_RESOURCES)['application'];

/** A link's kind in the cabinet's language (the application form's names); any other, as served. */
function linkKindName(link: PublicLink): string {
  return i18n.exists(`application:links.kinds.${link.kind}`)
    ? i18n.t(`application:links.kinds.${link.kind as keyof ApplicationTexts['links']['kinds']}`)
    : link.label;
}

/** A sign as the view carries it ("Cancer") in the cabinet's language; one it does not know, as is. */
function signName(sign: string): string {
  const code = sign.toLowerCase();
  return i18n.exists(`application:chart.sign.${code}`)
    ? i18n.t(`application:chart.sign.${code as keyof ApplicationTexts['chart']['sign']}`)
    : sign;
}

function Block({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className={classes.block}>
      <h2 className={classes.blockTitle}>{title}</h2>
      {children}
    </section>
  );
}

function Prose({ text, strong = false }: { readonly text: string; readonly strong?: boolean }) {
  return (
    <p className={strong ? `${classes.prose} ${classes.proseStrong}` : classes.prose}>{text}</p>
  );
}

/** The product's row, in its order, without the reads; a figure nobody measures is not drawn. */
function FigureRow({ figures }: { readonly figures: PublicFigures }): ReactNode {
  const { t } = useTranslation('profile');
  const cells = (['articles', 'followers'] as const).flatMap((key) => {
    const value = figures[key];
    return value === undefined ? [] : [{ key, value }];
  });
  return (
    <ul className={classes.figures}>
      {cells.map(({ key, value }) => (
        <li key={key} className={classes.figure}>
          <strong className={classes.figureValue}>{formatCount(value)}</strong>{' '}
          {/* What the figure counts, one and many - under its number, as the product's row words it. */}
          <span className={classes.figureLabel}>
            {t(`preview.figures.${key}`, { count: value })}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The author's public page as readers would see it, drawn from `view` - and the figures, if any. */
export function ProfilePreview({
  view,
  figures,
}: {
  readonly view: PublicView;
  readonly figures?: PublicFigures | undefined;
}): ReactNode {
  const { t } = useTranslation('profile');
  const name = view.displayName;
  const details = detailRows(view, t);
  const signs = view.signs;
  return (
    <article className={classes.page} aria-labelledby="profile-preview-name">
      {/* No name yet (an applicant who has only just started): the place it goes, named quietly. */}
      <h1
        id="profile-preview-name"
        className={name === null ? `${classes.name} ${classes.nameMissing}` : classes.name}
      >
        {name ?? t('preview.authorName')}
      </h1>

      <div className={classes.hero}>
        <div className={classes.media}>
          {view.avatarUrl === null ? (
            <div className={classes.photoEmpty} aria-hidden="true">
              <User size={48} />
            </div>
          ) : (
            <img
              className={classes.photo}
              src={view.avatarUrl}
              alt={name === null ? t('preview.photo') : t('preview.photoOf', { name })}
            />
          )}
        </div>

        <div className={classes.body}>
          {view.mainAreas.length > 0 || view.yearsOfPractice !== null ? (
            <ul className={classes.chips}>
              {view.mainAreas.map((area) => (
                <li key={area} className={`${classes.chip} ${classes.chipArea}`}>
                  {area}
                </li>
              ))}
              {/* "12 years of practice", beside the areas, as the product's page draws it. */}
              {view.yearsOfPractice === null ? null : (
                <li className={classes.chip}>{yearsWords(view.yearsOfPractice, t)}</li>
              )}
            </ul>
          ) : null}
          {view.headline === null ? null : <p className={classes.tagline}>{view.headline}</p>}
          {view.bioShort === null ? null : <p className={classes.lede}>{view.bioShort}</p>}
          {figures === undefined ? null : <FigureRow figures={figures} />}

          {view.experience !== null || view.approach !== null ? (
            <Block title={t('preview.blocks.experience')}>
              {view.experience === null ? null : <Prose text={view.experience} />}
              {view.approach === null ? null : <Prose text={view.approach} />}
            </Block>
          ) : null}

          {view.bioLong !== null || view.about !== null ? (
            <Block title={t('preview.blocks.bio')}>
              {view.bioLong === null ? null : <Prose text={view.bioLong} strong />}
              {view.about === null ? null : <Prose text={view.about} strong />}
            </Block>
          ) : null}

          {view.education === null ? null : (
            <Block title={t('preview.blocks.education')}>
              <Prose text={view.education} />
            </Block>
          )}

          {view.topics.length > 0 ? (
            <Block title={t('preview.blocks.topics')}>
              <ul className={classes.chips}>
                {view.topics.map((topic) => (
                  <li key={topic} className={`${classes.chip} ${classes.chipTopic}`}>
                    {topic}
                  </li>
                ))}
              </ul>
            </Block>
          ) : null}

          {signs === null ? null : (
            <Block title={t('preview.blocks.signs')}>
              <ul className={classes.signs}>
                <SignCell
                  mark={<Sun size={16} />}
                  label={t('preview.signs.sun')}
                  value={signName(signs.sun)}
                />
                {signs.moon === null ? null : (
                  <SignCell
                    mark={<Moon size={16} />}
                    label={t('preview.signs.moon')}
                    value={signName(signs.moon)}
                  />
                )}
                {signs.rising === null ? null : (
                  <SignCell
                    mark={<ArrowUp size={16} />}
                    label={t('preview.signs.rising')}
                    value={signName(signs.rising)}
                  />
                )}
              </ul>
            </Block>
          )}

          {details.length > 0 ? (
            <Block title={t('preview.blocks.details')}>
              <dl className={classes.details}>
                {details.map((row, index) => (
                  <div key={`${row.label}-${String(index)}`} className={classes.detail}>
                    <dt className={classes.detailLabel}>{row.label}</dt>
                    <dd className={classes.detailValue}>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </Block>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function SignCell({
  mark,
  label,
  value,
}: {
  readonly mark: ReactNode;
  readonly label: string;
  readonly value: string;
}): ReactNode {
  return (
    <li className={classes.sign}>
      <span className={classes.signMark} aria-hidden="true">
        {mark}
      </span>
      <span>
        <span className={classes.signLabel}>{label}</span>
        <span className={classes.signValue}>{value}</span>
      </span>
    </li>
  );
}
